import React, { useMemo, useState, useCallback } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { VehicleMaintenanceDto } from "../../../api/maintenanceApi";
import {
  DataTable,
  createColumn,
  createSearchableColumn,
  Column,
  Action,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";
import TireDiagram from "./TireDiagram";

interface MaintenanceTableProps {
  data: VehicleMaintenanceDto[];
  onEdit: (vehicleId: number) => void;
  isLoading?: boolean;
}

const formatMiles = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return value.toLocaleString();
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const alertTypeMap: Record<string, string[]> = {
  nowOil: ["OilChange"],
  nowTuneUp: ["TuneUp"],
  nowDpfFilter: ["DpfFilter"],
  nowAirDryFilter: ["AirDryFilterRecommended", "AirDryFilterRequired"],
  nowCoolant: ["Coolant"],
};

type StatusColor = "success" | "warning" | "error" | "default";

const getAlertStatus = (
  row: VehicleMaintenanceDto,
  field: string
): StatusColor => {
  const types = alertTypeMap[field];
  if (!types) return "default";
  const alert = row.activeAlerts.find((a) => types.includes(a.alertType));
  if (!alert) return "default";
  return alert.severity === "Critical" ? "error" : "warning";
};

const NowValue: React.FC<{
  value: number | null | undefined;
  row: VehicleMaintenanceDto;
  field: string;
}> = ({ value, row, field }) => {
  const status = getAlertStatus(row, field);
  const colorMap: Record<StatusColor, string> = {
    success: "success.main",
    warning: "warning.main",
    error: "error.main",
    default: "text.primary",
  };
  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: "0.8125rem",
        fontWeight: status !== "default" ? 600 : 400,
        color: colorMap[status],
      }}
    >
      {formatMiles(value)}
    </Typography>
  );
};

const CellText: React.FC<{
  children: React.ReactNode;
  fontWeight?: number;
}> = ({ children, fontWeight }) => (
  <Typography
    variant="body2"
    sx={{ fontSize: "0.8125rem", fontWeight: fontWeight ?? 400 }}
  >
    {children}
  </Typography>
);

const MaintenanceTable: React.FC<MaintenanceTableProps> = ({
  data,
  onEdit,
  isLoading,
}) => {
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>({});

  const filteredData = useMemo(() => {
    if (Object.keys(searchFilters).length === 0) return data;
    return data.filter((row) => {
      for (const [field, value] of Object.entries(searchFilters)) {
        if (!value) continue;
        const rowValue = (row as any)[field];
        if (typeof value === "string" && typeof rowValue === "string") {
          if (!rowValue.toLowerCase().includes(value.toLowerCase())) return false;
        }
      }
      return true;
    });
  }, [data, searchFilters]);

  const handleSearchChange = useCallback(
    (filters: Record<string, any>) => setSearchFilters(filters),
    []
  );

  const searchableColumns: SearchableColumn[] = useMemo(
    () => [createSearchableColumn("vehicleName", "Vehicle", "string")],
    []
  );
  const columns: Column[] = useMemo(
    () => [
      createColumn("vehicleName", "Vehicle", {
        flex: 1.2,
        minWidth: 150,
        sortable: true,
        sortType: "string",
        renderCell: (_value: string, row: VehicleMaintenanceDto) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {row.activeAlerts.length > 0 && (
              <Tooltip title={`${row.activeAlerts.length} active alert(s)`}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: row.activeAlerts.some(
                      (a) => a.severity === "Critical"
                    )
                      ? "error.main"
                      : "warning.main",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <CellText fontWeight={500}>{row.vehicleName}</CellText>
          </Box>
        ),
      }),

      // --- Now calculations first (most important) ---
      // Colors are driven by active alerts, not hardcoded thresholds
      createColumn("nowOil", "Now Oil", {
        width: 110,
        sortable: true,
        sortType: "number",
        renderCell: (v: number | null, row: VehicleMaintenanceDto) => (
          <NowValue value={v} row={row} field="nowOil" />
        ),
      }),
      createColumn("nowTuneUp", "Now Tune Up", {
        width: 110,
        sortable: true,
        sortType: "number",
        renderCell: (v: number | null, row: VehicleMaintenanceDto) => (
          <NowValue value={v} row={row} field="nowTuneUp" />
        ),
      }),
      createColumn("nowDpfFilter", "Now DPF", {
        width: 100,
        sortable: true,
        sortType: "number",
        renderCell: (v: number | null, row: VehicleMaintenanceDto) =>
          row.isDpfRemoved ? (
            <CellText>N/A</CellText>
          ) : (
            <NowValue value={v} row={row} field="nowDpfFilter" />
          ),
      }),
      createColumn("nowAirDryFilter", "Now Air Dry", {
        width: 110,
        sortable: true,
        sortType: "number",
        renderCell: (v: number | null, row: VehicleMaintenanceDto) => (
          <NowValue value={v} row={row} field="nowAirDryFilter" />
        ),
      }),
      createColumn("nowCoolant", "Now Coolant", {
        width: 110,
        sortable: true,
        sortType: "number",
        renderCell: (v: number | null, row: VehicleMaintenanceDto) => (
          <NowValue value={v} row={row} field="nowCoolant" />
        ),
      }),

      // --- Oil details ---
      createColumn("lastOilChange", "Oil (mi)", {
        width: 100,
        renderCell: (v: number | null) => <CellText>{formatMiles(v)}</CellText>,
      }),
      createColumn("oilChangeThreshold", "Oil Interval", {
        width: 100,
        renderCell: (v: number | null) => <CellText>{formatMiles(v)}</CellText>,
      }),
      createColumn("lastOilChangeDate", "Oil Date", {
        width: 120,
        renderCell: (v: string | null) => <CellText>{formatDate(v)}</CellText>,
      }),

      // --- Alignment ---
      createColumn("alignmentDate", "Alignment", {
        width: 120,
        renderCell: (v: string | null) => <CellText>{formatDate(v)}</CellText>,
      }),

      // --- Tires ---
      createColumn("tiresChanged", "Tires", {
        width: 80,
        renderCell: (v: unknown[], _row: VehicleMaintenanceDto) =>
          v.length > 0 ? (
            <Tooltip
              title={
                <TireDiagram
                  tires={v as VehicleMaintenanceDto["tiresChanged"]}
                  readOnly
                />
              }
            >
              <Chip
                label={`${v.length}/10`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: "0.75rem" }}
              />
            </Tooltip>
          ) : (
            <CellText>-</CellText>
          ),
      }),

      // --- Tune Up details ---
      createColumn("lastTuneUp", "Tune Up (mi)", {
        width: 110,
        renderCell: (v: number | null) => <CellText>{formatMiles(v)}</CellText>,
      }),
      createColumn("tuneUpDate", "Tune Up Date", {
        width: 120,
        renderCell: (v: string | null) => <CellText>{formatDate(v)}</CellText>,
      }),

      // --- DPF ---
      createColumn("dpfFilterClean", "DPF (mi)", {
        width: 100,
        renderCell: (v: number | null, row: VehicleMaintenanceDto) => (
          <CellText>
            {row.isDpfRemoved ? (
              <Chip label="Deleted" size="small" color="info" sx={{ fontSize: "0.75rem" }} />
            ) : (
              formatMiles(v)
            )}
          </CellText>
        ),
      }),

      // --- DEF ---
      createColumn("defChange", "DEF", {
        width: 100,
        renderCell: (v: string | null) => <CellText>{v || "-"}</CellText>,
      }),

      // --- Air Dry Filter details ---
      createColumn("airDryFilterChange", "Air Dry (mi)", {
        width: 110,
        renderCell: (v: number | null) => <CellText>{formatMiles(v)}</CellText>,
      }),

      // --- Coolant details ---
      createColumn("lastCoolantChange", "Coolant (mi)", {
        width: 110,
        renderCell: (v: number | null) => <CellText>{formatMiles(v)}</CellText>,
      }),

      // --- Other ---
      createColumn("winterDate", "Winter", {
        width: 120,
        renderCell: (v: string | null) => <CellText>{formatDate(v)}</CellText>,
      }),
      createColumn("motorkote", "MOTORKOTE", {
        width: 110,
        renderCell: (v: string | null) => <CellText>{v || "-"}</CellText>,
      }),
      createColumn("emissionExpDate", "Emission Exp", {
        width: 120,
        renderCell: (v: string | null) => <CellText>{formatDate(v)}</CellText>,
      }),
    ],
    []
  );

  const actions: Action<VehicleMaintenanceDto>[] = useMemo(
    () => [
      {
        icon: <EditIcon fontSize="small" />,
        label: "Edit Maintenance",
        onClick: (item: VehicleMaintenanceDto) => onEdit(item.vehicleId),
      },
    ],
    [onEdit]
  );

  return (
    <DataTable
      items={filteredData}
      columns={columns}
      actions={actions}
      isLoading={isLoading}
      totalCount={filteredData.length}
      getRowId={(row: VehicleMaintenanceDto) => row.vehicleId}
      enableSearch
      searchableColumns={searchableColumns}
      onSearchChange={handleSearchChange}
      enableSorting
      enableColumnReorder
    />
  );
};

export default MaintenanceTable;
